import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { MobileAppBadges } from "@/components/MobileAppBadges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { useToast } from "@/hooks/use-toast";
import { getBillingHistory, type BillingHistoryItem } from "@/lib/payment-service";
import { Calendar, Download, ExternalLink, Receipt } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

export default function BillingPage() {
  const breadcrumbs = useMemo(
    () => [
      { label: "Recipe Boxes", href: "/home" },
      { label: "Profile", href: "/profile" },
      { label: "Billing" },
    ],
    [],
  );
  usePageBreadcrumbs(breadcrumbs);

  const { user, profile, loading: authLoading } = useAuthProfile();
  const { toast } = useToast();
  const [billingHistory, setBillingHistory] = useState<BillingHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      loadBillingHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const loadBillingHistory = async () => {
    if (!user) {
      return;
    }

    setIsLoading(true);
    try {
      const result = await getBillingHistory(50);
      setBillingHistory(result.items);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error("Failed to load billing history", error);
      toast({
        variant: "destructive",
        title: "Failed to load billing history",
        description: "We couldn&apos;t load your billing history. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  if (authLoading) {
    return (
      <div className="container max-w-4xl mx-auto py-6 px-4 space-y-6">
        <Skeleton className="h-9 w-48" />
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container max-w-4xl mx-auto py-6 px-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">Please sign in to view your billing history.</p>
            <Button asChild className="mt-4">
              <Link to="/login">Sign In</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasActiveSubscription = profile?.platformSubscriptionId && profile?.subscriptionStatus === "active";

  if (!hasActiveSubscription) {
    return (
      <div className="container max-w-4xl mx-auto py-6 px-4 space-y-6">
        <div className="space-y-2">
          <h1 className="font-headline text-3xl font-semibold tracking-tight">Billing History</h1>
          <p className="text-muted-foreground">View your subscription and payment history.</p>
        </div>
        <Card>
          <CardContent className="pt-6 text-center space-y-4">
            <p className="text-muted-foreground">You don&apos;t have an active subscription.</p>
            <div className="max-w-md mx-auto">
              <MobileAppBadges />
            </div>
            <p className="text-sm text-muted-foreground">Subscription management is only available through the mobile app.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto py-6 px-4 space-y-6">
      <div className="space-y-2">
        <h1 className="font-headline text-3xl font-semibold tracking-tight">Billing History</h1>
        <p className="text-muted-foreground">View your subscription and payment history.</p>
      </div>

      <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
        <CardHeader>
          <CardTitle>Manage Your Subscription</CardTitle>
          <CardDescription>
            To upgrade, cancel, or change your subscription, please use the mobile app.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MobileAppBadges />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
          <CardDescription>All invoices and receipts for your subscription.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : billingHistory.length > 0 ? (
            <div className="space-y-3">
              {billingHistory.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-lg border p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="rounded-full bg-primary/10 p-2">
                      <Receipt className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{item.description}</p>
                        <Badge
                          variant={
                            item.status === "paid"
                              ? "default"
                              : item.status === "open"
                                ? "secondary"
                                : "destructive"
                          }
                          className="text-xs"
                        >
                          {item.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(item.date)}
                        </span>
                        <span className="font-medium text-foreground">
                          {formatCurrency(item.amount, item.currency)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.invoiceUrl && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={item.invoiceUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Invoice
                        </a>
                      </Button>
                    )}
                    {item.receiptUrl && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={item.receiptUrl} target="_blank" rel="noopener noreferrer">
                          <Download className="h-4 w-4 mr-2" />
                          Receipt
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {hasMore && (
                <div className="text-center pt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing recent payments. For older invoices, contact support.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No billing history available yet.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button variant="outline" asChild>
          <Link to="/profile">
            Back to Profile
          </Link>
        </Button>
      </div>
    </div>
  );
}

